/**
 * PosCustomDropdown is a customizable dropdown component for POS systems.
 * Supports single and multi-level dropdowns, optional icons.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} [props.fontSize] - Font size for dropdown text.
 * @param {string} [props.p] - Padding for dropdown.
 * @param {string|number} [props.width] - Width of the dropdown.
 * @param {string|number} [props.height] - Height of the dropdown.
 * @param {string|number} [props.radius] - Border radius of the dropdown.
 * @param {string} [props.border] - Border style for dropdown.
 * @param {string|number} [props.fontWeight] - Font weight for dropdown text.
 * @param {Array<Object>} props.dropdownData - Array of dropdown options. Each option may be single or multi-level.
 * @param {Array<Object>} [props.multiLevelData] - Array of multi-level data objects for dropdown groups.
 * @param {string} [props.placeholder] - Placeholder text when no selection is made.
 * @param {boolean} [props.isParentLeftIcon=false] - Show icon on the left of parent group label.
 * @param {boolean} [props.isParentRightIcon=false] - Show icon on the right of parent group label.
 * @param {boolean} [props.keyboard=false] - Enable global keyboard action.
 * @param {boolean} [props.enableScrollEffect=true] - Enable horizontal scroll/rolling text effect for selected value.
 * @param {Object} [props.selectedData] - Currently selected dropdown data.
 * @param {Function} props.setSelectedData - Function to update selected dropdown data.
 * @param {Function} [props.reduxDispatch] - Optional Redux dispatch function for license selection.
 * @param {Function} [props.addLicense] - Redux action creator for adding a license.
 * @param {Function} [props.handleLocation] - Optional handler for location selection/navigation.
 * @param {boolean} [props.isNavigating] - Flag to prevent navigation if true.
 * @param {Array<Object>} [props.retail] - Array of retail objects for location handling.
 * @returns {JSX.Element} The rendered dropdown component.
 *
 * @returns {Object} selectedData - The selected value object containing:
 *   - parentLabel: string
 *   - parentValue: any
 *   - childLabel: string (if multi-level selection)
 *   - childValue: any (if multi-level selection)
 *
 * @example
 * <PosCustomDropdown
 *   dropdownData={[{ label: "Retail", value: 1, isMultiLevel: true }]}
 *   multiLevelData={[{ levelOneId: 1, levelTwo: [{ label: "Branch A", value: "A" }] }]}
 *   placeholder="Select Retail"
 *   selectedData={selectedData}
 *   setSelectedData={setSelectedData}
 *   enableScrollEffect={true}
 * />
 *
 */
import React, { useEffect, useRef, useState } from "react";
import "./PosCustomDropdown.css";
import { ChevronDownIcon, MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import PosCustomIcon from "../PosCustomIcon/PosCustomIcon";
import { CircularProgress } from "@mui/material";

const PosCustomDropdown = ({
  fontSize,
  p,
  width,
  height,
  radius,
  border,
  fontWeight,
  dropdownData,
  multiLevelData,
  placeholder,
  isParentLeftIcon = false,
  isParentRightIcon = false,
  selectedData,
  setSelectedData,
  keyboard = false,
  enableScrollEffect = true,
  loader,
  minWidth,
  openUpward = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [highlightedChildIndex, setHighlightedChildIndex] = useState({});
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const calcMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const menuMaxHeight = 240;
    const openUpward = spaceBelow < menuMaxHeight && rect.top > menuMaxHeight;
    setMenuStyle({
      top: openUpward ? undefined : rect.bottom + 2,
      bottom: openUpward ? viewportHeight - rect.top + 2 : undefined,
      left: rect.left,
      width: rect.width,
    });
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (newState) {
        setHighlightedIndex(0);
        setHighlightedChildIndex({});
        setTimeout(calcMenuPosition, 0);
      } else {
        setHighlightedIndex(-1);
        setHighlightedChildIndex({});
      }
      return newState;
    });
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      // Close all other groups and toggle the clicked group
      const newExpandedGroups = { [groupId]: !prev[groupId] };

      // Reset highlighted child index for the toggled group
      if (newExpandedGroups[groupId]) {
        setHighlightedChildIndex((prev) => ({ ...prev, [groupId]: 0 }));
      } else {
        // setHighlightedChildIndex((prev) => ({ ...prev, [groupId]: -1 }));
        setHighlightedChildIndex((prev) => {
          const newState = { ...prev };
          delete newState[groupId];
          return newState;
        });
      }

      return newExpandedGroups;
    });
  };

  const handleSelect = (parent, child) => {
    let selectedObject = {};
    if (child) {
      selectedObject = {
        parentValue: parent?.value,
        parentLabel: parent?.label,
        childValue: child?.value,
        childLabel: child?.label,
        parentBranchSubType: child?.branchSubType ?? "",
      };
    } else {
      selectedObject = {
        parentValue: parent?.value,
        parentLabel: parent?.label,
        parentBranchSubType: parent?.branchSubType ?? "",
      };
    }
    setSelectedData(selectedObject);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setHighlightedChildIndex({});

    if (props.reduxDispatch) {
      props.reduxDispatch(
        props.addLicense({
          licenseId: parent?.value,
          licenseName: parent?.label,
        })
      );
    }

    if (props.handleLocation) {
      if (!props.isNavigating) {
        const branchId = selectedObject?.childValue;
        const branchSubType = selectedObject?.parentBranchSubType;
        const retailId = selectedObject?.parentValue;
        const parentDisplayName = selectedObject?.parentLabel;
        const childDisplayName = selectedObject?.childLabel;
        const retail = props.retail.find((item) => item?.retailId === selectedObject?.parentValue);
        props.handleLocation(
          branchId,
          retailId,
          "RETAIL",
          "PALLET_RETAIL",
          parentDisplayName,
          childDisplayName,
          retail,
          branchSubType
        );
      }
    }
  };

  // Close on outside click; reposition on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleReposition = () => calcMenuPosition();
    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!keyboard) return;
    const handleGlobalKeyDown = (event) => {
      // Global shortcut: open dropdown with ArrowDown if it's closed
      if (event.key === "ArrowDown" && !isOpen) {
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(-1);

        // auto-focus dropdown so local navigation works
        dropdownRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [isOpen, keyboard]);

  useEffect(() => {
    const handleLocalKeyDown = (event) => {
      if (!isOpen) return;

      event.preventDefault();
      switch (event.key) {
        case "Escape":
          setIsOpen(false);
          setHighlightedIndex(-1);
          setHighlightedChildIndex({});
          break;

        case "ArrowDown":
          const groupId = dropdownData[highlightedIndex]?.value;
          if (groupId && expandedGroups[groupId] && highlightedChildIndex[groupId] !== undefined) {
            const children =
              multiLevelData.find((entry) => entry.levelOneId === groupId)?.levelTwo || [];
            setHighlightedChildIndex((prev) => ({
              ...prev,
              [groupId]: Math.min((prev[groupId] || 0) + 1, children.length - 1),
            }));
          } else {
            setHighlightedIndex((prev) => Math.min(prev + 1, dropdownData.length - 1));
          }
          break;

        case "ArrowUp": {
          const groupId = dropdownData[highlightedIndex]?.value;

          // If current parent has expanded children -> move inside them
          if (groupId && expandedGroups[groupId] && highlightedChildIndex[groupId] !== undefined) {
            setHighlightedChildIndex((prev) => ({
              ...prev,
              [groupId]: Math.max((prev[groupId] || 0) - 1, 0),
            }));
          } else {
            // Otherwise move between parent options
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          }
          break;
        }

        case "Enter":
          event.preventDefault();
          event.stopPropagation();
          if (dropdownData[highlightedIndex]) {
            const option = dropdownData[highlightedIndex];
            if (option.isMultiLevel) {
              toggleGroup(option.value);
            } else {
              handleSelect(option);
            }
            const groupId = option.value;
            if (expandedGroups[groupId]) {
              const children =
                multiLevelData.find((entry) => entry.levelOneId === groupId)?.levelTwo || [];
              if (children[highlightedChildIndex[groupId]]) {
                handleSelect(option, children[highlightedChildIndex[groupId]]);
              }
            }
          }
          break;

        case "ArrowRight":
          if (dropdownData[highlightedIndex]?.isMultiLevel) {
            toggleGroup(dropdownData[highlightedIndex].value);
          }
          break;

        case "ArrowLeft":
          if (dropdownData[highlightedIndex]?.isMultiLevel) {
            toggleGroup(dropdownData[highlightedIndex].value);
          }
          break;

        default:
          break;
      }
    };

    const dropdownElement = dropdownRef.current;
    dropdownElement?.addEventListener("keydown", handleLocalKeyDown);
    return () => {
      dropdownElement?.removeEventListener("keydown", handleLocalKeyDown);
    };
  }, [
    isOpen,
    highlightedIndex,
    highlightedChildIndex,
    expandedGroups,
    dropdownData,
    multiLevelData,
  ]);

  // Render the selected text with or without scroll effect
  const renderSelectedText = () => {
    const displayText = `${selectedData?.parentLabel}${
      selectedData?.childLabel ? ` - ${selectedData?.childLabel}` : ""
    }`;

    if (enableScrollEffect) {
      return (
        <div className="rolling-text-wrapper">
          <div className="rolling-text">
            <span>{displayText}</span>
            <span>{displayText}</span>
          </div>
        </div>
      );
    } else {
      return <span>{displayText}</span>;
    }
  };

  return (
    <div
      className="pos-dropdown-wrapper"
      style={{ width, ...(minWidth ? { minWidth } : {}) }}
      ref={dropdownRef}
      tabIndex={0}
    >
      <div
        ref={triggerRef}
        className="pos-custom-dropdown"
        style={{
          fontSize: fontSize || "0.813rem",
          padding: p || "0.525rem 1rem",
          height: height,
          border: border || "1px solid var(--surface-g2)",
          borderRadius: radius,
          fontWeight: fontWeight || "400",
          color: "var(--surface-g3)",
        }}
        onClick={toggleDropdown}
      >
        {!loader && selectedData?.parentLabel ? renderSelectedText() : placeholder}

        {!loader && <ChevronDownIcon className="custom-dropdown-arrow" />}
      </div>

      {isOpen && (
        <div className={openUpward ? "dropdown-menu-upward" : "dropdown-menu"}>
          {dropdownData && dropdownData.length > 0 ? (
            dropdownData.map((option, index) => {
              const isHighlighted = index === highlightedIndex;
              if (option.isMultiLevel) {
                const children = multiLevelData.find(
                  (entry) => entry.levelOneId === option.value
                )?.levelTwo;

                return (
                  <div key={index} className="dropdown-group">
                    <div
                      className={`dropdown-group-label ${isHighlighted ? "highlighted" : ""}`}
                      onClick={() => toggleGroup(option.value)}
                    >
                      {isParentLeftIcon && !isParentRightIcon && (
                        <span>
                          <PosCustomIcon
                            icon={
                              expandedGroups[option.value] ? (
                                <MinusCircleIcon />
                              ) : (
                                <PlusCircleIcon />
                              )
                            }
                            height={"16px"}
                            width={"16px"}
                            color="#949494"
                          />
                        </span>
                      )}
                      {option.label}
                      {!isParentLeftIcon && isParentRightIcon && (
                        <span>
                          <PosCustomIcon
                            icon={
                              expandedGroups[option.value] ? (
                                <MinusCircleIcon />
                              ) : (
                                <PlusCircleIcon />
                              )
                            }
                            height={"16px"}
                            width={"16px"}
                            color="#949494"
                          />
                        </span>
                      )}
                    </div>
                    {expandedGroups[option.value] && (
                      <div className="dropdown-submenu">
                        {children?.map((child, childIndex) => (
                          <div
                            key={child.value}
                            className={`dropdown-item ${
                              childIndex === highlightedChildIndex[option.value]
                                ? "highlighted"
                                : ""
                            }`}
                            onClick={() => handleSelect(option, child)}
                          >
                            {child.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div
                    key={option.value}
                    className={`dropdown-item ${isHighlighted ? "highlighted" : ""}`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </div>
                );
              }
            })
          ) : (
            <div className="dropdown-item disabled">No data found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PosCustomDropdown;